import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Group,
  SimpleGrid,
  Paper,
  Text,
  Table,
  Progress,
} from "@mantine/core";
import moment from "moment";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useQuizBasePath } from "../../hooks/useQuizBasePath";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import QuizStatCards from "../../components/quiz/QuizStatCards";
import QuizStatusBadge from "../../components/quiz/QuizStatusBadge";

const apiEntity = "quiz";

const QuizStatsPage = () => {
  const navigate = useNavigate();
  const tabNav = useTabNavigate();
  const basePath = useQuizBasePath();
  const [api] = useServerApi();
  const [listStats, setListStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, listRes] = await Promise.all([
          api.Quiz.getListStatistics(),
          api.search({
            apiEntity,
            searchQuery: {},
            currentPage: 1,
            pageSize: 200,
            sort: { by: "createdAt", order: "desc" },
          }),
        ]);

        setListStats(statsRes?.data ?? {});
        const docs = listRes?.docs ?? [];

        const enriched = await Promise.all(
          docs.map(async (quiz) => {
            const statRes = await api.Quiz.getStatistics(quiz._id);
            return { quiz, stats: statRes?.data ?? {} };
          })
        );
        setRows(enriched);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate(basePath);
  };

  return (
    <Page
      title="Quiz Statistics"
      actions={
        <Button variant="default" onClick={handleClose}>
          Close
        </Button>
      }
    >
      <QuizStatCards stats={listStats} />

      <Paper p="md" withBorder mt="lg">
        <Text weight={600} mb="md">
          Per-Quiz Breakdown
        </Text>
        {loading && <Text color="dimmed">Loading...</Text>}
        {!loading && (
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>Quiz</th>
                <th>Status</th>
                <th>Deadline</th>
                <th>Participants</th>
                <th>Passed</th>
                <th>Pending</th>
                <th>Pass Rate</th>
                <th>Avg Score</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ quiz, stats }) => {
                const passRate =
                  stats.total > 0
                    ? Math.round((stats.passed / stats.total) * 100)
                    : 0;
                return (
                  <tr key={quiz._id}>
                    <td>
                      <Text weight={500}>{quiz.title}</Text>
                      <Text size="xs" color="dimmed">
                        {quiz.code}
                      </Text>
                    </td>
                    <td>
                      <QuizStatusBadge status={quiz.status} />
                    </td>
                    <td>
                      {quiz.deadline
                        ? moment(quiz.deadline).format("YYYY-MM-DD")
                        : "--"}
                    </td>
                    <td>{stats.total ?? 0}</td>
                    <td>{stats.passed ?? 0}</td>
                    <td>{stats.pending ?? 0}</td>
                    <td style={{ minWidth: 120 }}>
                      <Text size="sm">{passRate}%</Text>
                      <Progress value={passRate} size="sm" mt={4} />
                    </td>
                    <td>{stats.averageScore ?? 0}%</td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={8}>
                    <Text color="dimmed">No quizzes found</Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Paper>
    </Page>
  );
};

export default QuizStatsPage;
